import type { Money } from '@dastarhan/contracts';

/**
 * Заказ как чистый редьюсер событий. Один и тот же код гоняет
 * состояние на кассе и материализует чек на сервере — расхождения
 * между «что видел кассир» и «что легло в базу» исключены by design.
 */

export type OrderMode = 'DINE_IN' | 'TAKEAWAY' | 'DELIVERY';

export interface ItemModifier {
  name: string;
  priceDelta: Money;
  /** себестоимость добавки — снапшот момента продажи */
  cost?: Money;
  /** «без лука»: ингредиент, который не уйдёт со склада */
  removes?: { productId: string; qty: number };
}

export interface OrderItem {
  itemId: string;
  productId: string | null;
  name: string;
  qty: number;
  unitPrice: Money;            // база без модификаторов
  modifiers: ItemModifier[];
  removed: boolean;
  /** курс подачи: 0 — срочно, 1..4; undefined — первая волна */
  course?: number;
  /**
   * Позиция ушла на кухню. Повар режет мясо прямо сейчас — менять
   * состав или снимать такую строку официант уже не вправе: продукты
   * потрачены, и решение об этом принимает старший.
   *
   * Признак живёт В САМОЙ позиции, а не в отдельном списке на кассе:
   * список умирает вместе с перезагрузкой страницы, а блюдо на плите
   * остаётся. Так он переживает и обновление, и передачу стола
   * другому официанту.
   */
  fired?: boolean;
}

/** Сумма строки: qty × (база + Σ добавок), округление один раз. */
export function lineSum(i: Pick<OrderItem, 'qty' | 'unitPrice' | 'modifiers'>): Money {
  const delta = i.modifiers.reduce((s, m) => s + m.priceDelta, 0);
  return Math.round(i.qty * (i.unitPrice + delta));
}

/** Ключ набора модификаторов: порядок выбора не создаёт «разные» строки. */
function modsKey(mods: ItemModifier[]): string {
  return mods.map((m) => `${m.name}:${m.priceDelta}`).sort().join('|');
}

export interface OrderState {
  orderId: string;
  number: number;
  mode: OrderMode;
  openedAt: string;
  /** кто набрал заказ (официант) — переносится при передаче стола */
  openedBy?: { id: string; name: string };
  /**
   * Комментарий ко всему чеку: «день рождения», «столик у окна просили
   * не беспокоить». Пишется для тех, кто увидит чек потом — кассира,
   * менеджера, владельца в отчёте. Кухне это не уходит: у неё свои
   * заметки на позициях
   */
  comment?: string;
  items: OrderItem[];
}

export type OrderAction =
  | { type: 'opened'; orderId: string; number: number; mode: OrderMode; at: string }
  | { type: 'item.added'; itemId: string; productId: string | null; name: string; qty: number; unitPrice: Money; modifiers?: ItemModifier[] }
  | { type: 'item.removed'; itemId: string }
  | { type: 'item.qty'; itemId: string; qty: number }
  | { type: 'mode'; mode: OrderMode }
  | { type: 'item.course'; itemId: string; course: number }
  | { type: 'items.fired'; itemIds: string[] }
  | { type: 'comment'; text: string };

export function reduceOrder(state: OrderState | null, a: OrderAction): OrderState {
  switch (a.type) {
    case 'opened':
      return { orderId: a.orderId, number: a.number, mode: a.mode, openedAt: a.at, items: [] };
    case 'item.added': {
      if (!state) throw new Error('ORDER_NOT_OPENED');
      const mods = a.modifiers ?? [];
      // Тот же товар С ТЕМ ЖЕ набором модификаторов подряд — наращиваем
      // количество; «острый лагман» и «обычный лагман» — разные строки
      const last = state.items.filter((i) => !i.removed).at(-1);
      if (
        last && last.productId !== null &&
        /*
         * Отправленная строка закрыта для слияния: добавка после
         * «Заказать» обязана стать НОВОЙ строкой. Иначе количество
         * растёт на строке с флагом «на кухне», добор никогда не
         * уезжает повару, а гость платит за неприготовленное
         */
        !last.fired &&
        last.productId === a.productId &&
        last.unitPrice === a.unitPrice &&
        modsKey(last.modifiers) === modsKey(mods)
      ) {
        return {
          ...state,
          items: state.items.map((i) =>
            i.itemId === last.itemId ? { ...i, qty: i.qty + a.qty } : i,
          ),
        };
      }
      return {
        ...state,
        items: [...state.items, {
          itemId: a.itemId, productId: a.productId, name: a.name,
          qty: a.qty, unitPrice: a.unitPrice, modifiers: mods, removed: false,
        }],
      };
    }
    case 'item.removed': {
      if (!state) throw new Error('ORDER_NOT_OPENED');
      return { ...state, items: state.items.map((i) => (i.itemId === a.itemId ? { ...i, removed: true } : i)) };
    }
    case 'comment': {
      if (!state) return state as unknown as OrderState;
      return { ...state, comment: a.text || undefined };
    }

    case 'items.fired': {
      // Отметка необратима: снять её нельзя ни одним действием.
      // Блюдо либо приготовлено, либо списано с причиной — третьего
      // состояния у потраченных продуктов не бывает
      if (!state) return state as unknown as OrderState;
      const fired = new Set(a.itemIds);
      return {
        ...state,
        items: state.items.map((i) =>
          fired.has(i.itemId) ? { ...i, fired: true } : i),
      };
    }

    case 'item.course': {
      // Курс — свойство строки, а не отдельная сущность: перенос блюда
      // в другую волну не должен ломать номер и сумму
      if (!state) return state as unknown as OrderState;
      return {
        ...state,
        items: state.items.map((i) =>
          i.itemId === a.itemId ? { ...i, course: a.course } : i),
      };
    }
    case 'mode': {
      if (!state) throw new Error('ORDER_NOT_OPENED');
      return { ...state, mode: a.mode };
    }
    case 'item.qty': {
      if (!state) throw new Error('ORDER_NOT_OPENED');
      if (a.qty <= 0) return reduceOrder(state, { type: 'item.removed', itemId: a.itemId });
      return { ...state, items: state.items.map((i) => (i.itemId === a.itemId ? { ...i, qty: a.qty } : i)) };
    }
  }
}

/**
 * Итоги заказа. Сервисный сбор считается ПОСЛЕ скидки: гость платит
 * процент с того, что реально отдаёт, а не с зачёркнутой цены.
 * Порядок здесь единственный на всю систему — касса, чек и отчёт
 * не имеют права посчитать по-разному.
 */
export function orderTotals(state: OrderState, discount: Money = 0, servicePct = 0) {
  const live = state.items.filter((i) => !i.removed);
  const subtotal = live.reduce((s, i) => s + lineSum(i), 0);
  const afterDiscount = Math.max(0, subtotal - discount);
  const service = servicePct > 0
    ? Math.round((afterDiscount * servicePct) / 100)
    : 0;
  return {
    subtotal,
    discount,
    service,
    servicePct,
    total: afterDiscount + service,
    positions: live.length,
  };
}
